import React from 'react';
import styled from 'styled-components';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalSlideIn 0.2s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 16px;
`;

const getModalIcon = (variant?: 'danger' | 'warning' | 'info') => {
  switch (variant) {
    case 'danger': return '🚨';
    case 'warning': return '⚠️';
    case 'info': return 'ℹ️';
    default: return '❓';
  }
};

const ModalTitle = styled.h3<{ variant?: 'danger' | 'warning' | 'info' }>`
  color: ${props => {
    switch (props.variant) {
      case 'danger': return '#dc2626';
      case 'warning': return '#d97706';
      case 'info': return '#2563eb';
      default: return '#1f2937';
    }
  }};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '${props => getModalIcon(props.variant)}';
    font-size: 1.5rem;
  }
`;

const ModalMessage = styled.p`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'danger' | 'secondary' }>`
  background: ${props => {
    switch (props.variant) {
      case 'danger': return '#dc2626';
      case 'primary': return '#667eea';
      case 'secondary': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'danger': return 'white';
      case 'primary': return 'white';
      case 'secondary': return '#374151';
      default: return '#374151';
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'danger': return '#dc2626';
      case 'primary': return '#667eea';
      case 'secondary': return '#d1d5db';
      default: return '#d1d5db';
    }
  }};
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
  
  &:hover {
    background: ${props => {
      switch (props.variant) {
        case 'danger': return '#b91c1c';
        case 'primary': return '#5a67d8';
        case 'secondary': return '#e5e7eb';
        default: return '#e5e7eb';
      }
    }};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${props => {
      switch (props.variant) {
        case 'danger': return 'rgba(220, 38, 38, 0.1)';
        case 'primary': return 'rgba(102, 126, 234, 0.1)';
        default: return 'rgba(156, 163, 175, 0.1)';
      }
    }};
  }
`;

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Conferma',
  cancelText = 'Annulla',
  onConfirm,
  onCancel,
  variant = 'danger'
}) => {
  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onCancel]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle variant={variant}>{title}</ModalTitle>
        </ModalHeader>
        <ModalMessage>{message}</ModalMessage>
        <ModalActions>
          <ModalButton variant="secondary" onClick={onCancel}>
            {cancelText}
          </ModalButton>
          <ModalButton variant={variant === 'info' ? 'primary' : 'danger'} onClick={onConfirm}>
            {confirmText}
          </ModalButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ConfirmationModal;